import React from "react"
import { Accordion, AccordionDetails, AccordionSummary, Stack, Typography } from "@mui/material"
import { useParams } from "react-router"
import { AlertProps } from '@mui/material/Alert';
import ExpandMore from "@mui/icons-material/ExpandMore"

import { useSnackbar } from '../../../contexts/SnackBarContext';

import { getWorkshopTestingTree, gettestingpairdata, WSTestingTreeValue, WSTestingKeyValue } from '../../types/WSTestingData';
import { WSTestingProductPairValue, WorkshopProductPairTestCaseResult, updateWsResultwCounter } from '../../types/WSTestingData';
import { WorkshopTestResult } from "../../types/WorkshopResult";
import { WSTestingProdTree } from "./WSTestingProdTree";
import { WSTestingRecordPairingTable } from "./WSTestingRecordPairingTable";
import { TestingPairServerData } from "./TestingPairServerData";
import { PageAlternate, BreadCrumbProps } from "../../subs/PageAlternate";
import { WSTRiFaceAccordion } from "./WSTRiFaceAccordion";
import { WSTestcaseRecording } from "./WSTestcaseRecording";
import { copyArrayWithIndexChanged } from "../../Utils";
import { WSTestingChipFilterContext } from "./WSTestingChipFilterContext";

const refreshInterval = 60000; // 60 seconds
const updateTreeCounter = (keyVal: WSTestingKeyValue, treeValues: WSTestingTreeValue[], increment: WorkshopTestResult, decrement: WorkshopTestResult): WSTestingTreeValue[] => {
    let treeValuesLoc = treeValues;

    treeValues.forEach(tv => {
        if (tv.key.wsProdUpperId === keyVal.wsProdUpperId) {
            if (tv.key.testCombinationId === keyVal.testCombinationId || tv.key.testCombinationId === 0) {
                if (tv.key.wsTreeEntryType === keyVal.wsTreeEntryType || tv.key.wsTreeEntryType === 0) {
                    if (tv.key.interfaceId === keyVal.interfaceId || tv.key.interfaceId === 0) {
                        if (tv.key.companyId === keyVal.companyId || tv.key.companyId === 0) {
                            if (tv.key.wsProdLowerId === keyVal.wsProdLowerId || tv.key.wsProdLowerId === 0) {
                                // this is the node we need to update
                                switch (increment) {
                                    case WorkshopTestResult.Passed:
                                        tv.testsPassed = tv.testsPassed + 1;
                                        break;
                                    case WorkshopTestResult.Failed:
                                        tv.testsFailed = tv.testsFailed + 1;
                                        break;
                                    case WorkshopTestResult.Warning:
                                        tv.testsWarning = tv.testsWarning + 1;
                                        break;
                                    case WorkshopTestResult.NotComplete:
                                        tv.testsNotCompleted = tv.testsNotCompleted + 1;
                                        break;
                                }
                                switch (decrement) {
                                    case WorkshopTestResult.Passed:
                                        tv.testsPassed = tv.testsPassed - 1;
                                        break;
                                    case WorkshopTestResult.Failed:
                                        tv.testsFailed = tv.testsFailed - 1;
                                        break;
                                    case WorkshopTestResult.Warning:
                                        tv.testsWarning = tv.testsWarning - 1;
                                        break;
                                    case WorkshopTestResult.NotComplete:
                                        tv.testsNotCompleted = tv.testsNotCompleted - 1;
                                        break;
                                }
                                copyArrayWithIndexChanged(treeValuesLoc, treeValuesLoc.findIndex(t => t.key.wsProdUpperId === tv.key.wsProdUpperId &&
                                    t.key.testCombinationId === tv.key.testCombinationId && t.key.wsTreeEntryType === tv.key.wsTreeEntryType &&
                                    t.key.interfaceId === tv.key.interfaceId && t.key.companyId === tv.key.companyId && t.key.wsProdLowerId === tv.key.wsProdLowerId), tv);
                            }
                        }
                    }
                }
            }
        }
    });

    return treeValuesLoc;
}

export const WSTestingRecords = () => {
    const { id, disabled } = useParams();
    const { showSnackbar } = useSnackbar();

    const [workshopId, setWorkshopId] = React.useState<number>(0);
    const [selCompanyId, setCompanyId] = React.useState<number>(0);
    const [productpairStr, setProductPairStr] = React.useState<string>("");
    const [recordingDisabled, setRecordingDisabled] = React.useState<boolean>(true);
    const [treeValues, setTreeValues] = React.useState<WSTestingTreeValue[]>([]);
    const [productPairData, setProductPairData] = React.useState<WSTestingProductPairValue | null>(null);
    const [pendingResultFilter, setPendingResultFilter] = React.useState<WorkshopTestResult | null>(null);
    const [serverPropsExpanded, setServerPropsExpanded] = React.useState<boolean>(true);

    const showUserFeedback = React.useCallback((msg: string | null, severity: AlertProps['severity'] = 'error') => {
        msg && showSnackbar(msg, severity);
    }, [showSnackbar]);

    const fetch = React.useCallback(async (id: number) => {
        try {
            if (id === 0) return;

            const wsTestingTreeResponse = await getWorkshopTestingTree(id, new AbortController());

            if (wsTestingTreeResponse.failed) {
                showUserFeedback(wsTestingTreeResponse.errorText);
                return;
            }

            const treeValuesLoc = wsTestingTreeResponse.result;
            setTreeValues(treeValuesLoc);
            console.log(`fetched tree values: ${JSON.stringify(treeValuesLoc)}`);

        } catch (error) {
            showUserFeedback(String(error));
        }
    }, [showUserFeedback]);

    // after 60 secs of idle (or not recording new results) refresh the results
    React.useEffect(() => {
        console.log(`initializing Result Refresh Timer`);
        const interval = setInterval(() => {
            fetch(workshopId);
        }, refreshInterval);

        return () => {
            console.log(`clearing Result Refresh Timer`);
            clearInterval(interval);
        };
    }, [fetch, workshopId]);

    React.useEffect(() => {
        if (Number(id) === 0) return;
        if (Number(id) === workshopId) return;
        setWorkshopId(Number(id));

        fetch(Number(id));
    }, [id, fetch, workshopId]);

    const fetchProductPair = React.useCallback(async (productpair: string) => {
        try {
            if (productpair === '0') return;

            const wsTestingPairResponse = await gettestingpairdata(productpair, new AbortController());

            if (wsTestingPairResponse.failed) {
                showUserFeedback(wsTestingPairResponse.errorText);
                return;
            }

            const wsTestingPairLoc = (await wsTestingPairResponse).result;
            setProductPairData(wsTestingPairLoc);

        } catch (error) {
            showUserFeedback(String(error));
        }
    }, [showUserFeedback]);

    const changeTestCaseResultHandler = (testCase: WorkshopProductPairTestCaseResult) => {
        saveResultsHandler(testCase);
    }

    const saveResultsHandler = async (testCase: WorkshopProductPairTestCaseResult) => {
        try {
            if (!productPairData) return; // should not happen, but to ensure we have the necessary data to build the request
            if (recordingDisabled) return; // should not happen, but to ensure we are allowed to record results

            // first we need to determine what has changed to calculate the counter updates at theend of this process
            let increment: WorkshopTestResult = WorkshopTestResult.ClientNotSupported;  // this should not be allowed, so use as dummy
            let decrement: WorkshopTestResult = WorkshopTestResult.ClientNotSupported;

            let productPairDataLoc = productPairData;
            const oldResult = productPairDataLoc.testCaseResults.find(tc => tc.id === testCase.id);
            if (oldResult) {
                increment = testCase.result;
                decrement = oldResult.result;
            }

            // need to update the result in the DB
            let response = await updateWsResultwCounter(testCase, new AbortController());
            if (response.failed) {
                showUserFeedback(response.errorText);
                return;
            }

            // for some reason, this doesn't trigger a re-render...
            /*
            testCase = response.result;
            if (productPairDataLoc.testCaseResults) {
                productPairDataLoc.version = productPairDataLoc.version + 1; // to ensure any component depending on this data know it has changed and re-render if needed
                const index = productPairDataLoc.testCaseResults.findIndex(wp => wp.id === testCase.id);
                productPairDataLoc.testCaseResults = copyArrayWithIndexChanged(productPairDataLoc.testCaseResults, index, testCase);
            }
            setProductPairData(null);
            setProductPairData(productPairDataLoc);
            */

            // until the above can be corrected, call a relaod from the api, this works
            fetchProductPair(productpairStr);

            // then we should build a key to find matchs in the tree and update the counters (our results only, results from others to be updated on refresh)
            if (increment !== decrement) {
                let treeValuesLoc = treeValues;
                let keyVal: WSTestingKeyValue = {
                    wsProdUpperId: productPairDataLoc.serverProduct.wsProductId,
                    testCombinationId: productPairDataLoc.testCombinationId,
                    wsTreeEntryType: 2,     // only consumer side should be updated when recording results, provider side is readonly
                    interfaceId: productPairDataLoc.interfaceId,
                    companyId: selCompanyId,
                    wsProdLowerId: productPairDataLoc.clientProduct.wsProductId
                }

                treeValuesLoc = updateTreeCounter(keyVal, treeValuesLoc, increment, decrement);

                keyVal.wsProdUpperId = productPairDataLoc.clientProduct.wsProductId;
                keyVal.wsProdLowerId = productPairDataLoc.serverProduct.wsProductId;
                treeValuesLoc = updateTreeCounter(keyVal, treeValuesLoc, increment, decrement);
                setTreeValues(treeValuesLoc);
            }
            showUserFeedback("Result saved successfully", "success");

        } catch (error) {
            showUserFeedback(String(error));
        }
    };

    const commonSelectHandler = (nodeHierarchy: string[]) => {
        //nodeHierarchy = WSTestingKeyValue values in order [wsProdUpperId, testCombinationId, wsTreeEntryType, interfaceId, companyId, wsProdLowerId]
        // first ensure the nodeHierarchy is valid and has the expected length
        if (!nodeHierarchy || nodeHierarchy.length !== 6) {
            setProductPairData(null);
            setProductPairStr("0");
            setRecordingDisabled(true);
            return;
        }
        const wsProdUpperId = nodeHierarchy[0];
        const testCombinationId = nodeHierarchy[1];
        const wsTreeEntryType = nodeHierarchy[2];
        const interfaceId = nodeHierarchy[3];
        const companyId = nodeHierarchy[4];
        const wsProdLowerId = nodeHierarchy[5];

        // productpPair = "{clientId}|{testCombinationId}|{interfaceId}|{serverId}"
        let productpair: string = '';
        if (wsTreeEntryType === '1') {
            // top level provider node, show provider pair data - is readonly
            productpair = `${wsProdLowerId}-${testCombinationId}-${interfaceId}-${wsProdUpperId}`;
            setRecordingDisabled(true);
        }
        else if (wsTreeEntryType === '2') {
            // top level consumer node, show consumer pair data - entry allowed
            productpair = `${wsProdUpperId}-${testCombinationId}-${interfaceId}-${wsProdLowerId}`;
            setRecordingDisabled(false);
        }
        setCompanyId(Number(companyId));
        setProductPairStr(productpair);
        fetchProductPair(productpair);
    }

    // Invoked when a user clicks a result count chip in the tree (via WSTestingChipFilterContext):
    // selects the product pairing as usual, then flags which result type should be filtered to
    // once WSTestcaseRecording mounts/updates for that pairing.
    const selectResultFilter = (nodeHierarchy: string[], resultType: WorkshopTestResult) => {
        commonSelectHandler(nodeHierarchy);
        setPendingResultFilter(resultType);
        setServerPropsExpanded(false);
    }

    const breadcrumbs: BreadCrumbProps[] = [
        { to: "/TestingTab", text: "Workshop Selection" },
        { to: `/WSTesting/${id}`, text: "Test Configuration (Legacy)" },
        { to: `/wstestingconfig/${id}`, text: "Configure" },
    ];

    return <WSTestingChipFilterContext.Provider value={{ selectResultFilter }}>
        <PageAlternate backButton
        heading={<WSTestingRecordHeader productPairData={productPairData} />}
        sidePanel={<WSTestingProdTree treeValues={treeValues} onSelectHandler={commonSelectHandler} />}
        breadcrumbs={Boolean(disabled) ? undefined : breadcrumbs}>
        {[<Stack rowGap={20}>
            <WSTestingRecordPairingTable productPairData={productPairData} />
            {(!productPairData) ? <Typography variant='h5'>Please select a product to get started</Typography> :
                <Typography variant='h5'>Testing information and results</Typography>}
            {productPairData && <>
                <Accordion defaultExpanded expanded={serverPropsExpanded} onChange={() => setServerPropsExpanded(!serverPropsExpanded)}>
                    <AccordionSummary expandIcon={<ExpandMore />}><Typography variant='bold'>Server Information</Typography></AccordionSummary>
                    <AccordionDetails>
                        <TestingPairServerData productPairData={productPairData}/>
                    </AccordionDetails >
                    {<WSTRiFaceAccordion productPairData={productPairData} />}
                </Accordion >
            </>}
            {
                (productPairData && ((productPairData?.serverProduct.readyToTest && productPairData?.clientProduct.readyToTest) || disabled)) ?
                    <WSTestcaseRecording productPairData={productPairData} disabled={recordingDisabled} changeTestCaseResultHandler={changeTestCaseResultHandler}
                        pendingResultFilter={pendingResultFilter} onPendingResultFilterConsumed={() => setPendingResultFilter(null)} /> :
                    <Typography variant='h5'>The selected products are not ready for testing</Typography>
            }
        </Stack >]}
        </PageAlternate>
    </WSTestingChipFilterContext.Provider>
}

export const WSTestingRecordHeader = ({ productPairData }:
    { productPairData: WSTestingProductPairValue | null }) => {
    const headerStr = `${productPairData?.clientProduct.productName ?? ""} ${productPairData?.clientProduct.productVersion ?? ""}`;

    return <Stack direction={'row'} justifyContent={'space-between'}>
        <Typography variant='h4'>{headerStr}</Typography>
    </Stack>
}